/**
 * CostMetricsProvider — Phase 5.1 (pilot).
 * Owns Cost Metrics data (via useCostMetricsData hook), derived monthList,
 * dashboard UI state, and status. Behaviour-preserving.
 *
 * Field names intentionally mirror legacy FintechContextProvider so consumer
 * migration is a pure `useFinTechContext()` → `useCostMetrics()` swap.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from 'src/logger';
import { TICKETS_FOR_OE_ERROR_URL } from 'src/constants/AppConstants';
import { getUniqueFieldValues } from 'src/utilities/CommonUtilities';
import { FiltersStatus } from './SharedFiltersProvider';
import { useNotification } from './NotificationProvider';
import { INITIAL_COST_METRICS_TAB_DATA } from '../../cost-metrics/CostMetricsConstants';
import { FinTechOpsCategories, FinTechOpsMessages } from '../../FinTechOpsConstants';
import { CostMetricsDashboardItems, CostMetricsParsedDetails } from '../../cost-metrics/CostMetricsInterface';
import { useCostMetricsData } from '../hooks/useCostMetricsData';

export interface CostMetricsContextValue {
  finTechOpsCostMetricsDataStatus: FiltersStatus;
  setFinTechOpsCostMetricsDataStatus: (s: FiltersStatus) => void;

  finTechOpsCostMetricsParsedData: CostMetricsParsedDetails;
  setFinTechOpsCostMetricsParsedData: (d: CostMetricsParsedDetails) => void;

  finTechOpsCostMetricsDashboardData: CostMetricsDashboardItems;
  setFinTechOpsCostMetricsDashboardData: (d: CostMetricsDashboardItems) => void;

  finTechOpsCostMetricsMonthList: string[];
  setFinTechOpsCostMetricsMonthList: (m: string[]) => void;
}

const EMPTY_PARSED = {} as CostMetricsParsedDetails;

const defaultValue: CostMetricsContextValue = {
  finTechOpsCostMetricsDataStatus: 'loading',
  setFinTechOpsCostMetricsDataStatus: () => {},
  finTechOpsCostMetricsParsedData: EMPTY_PARSED,
  setFinTechOpsCostMetricsParsedData: () => {},
  finTechOpsCostMetricsDashboardData: INITIAL_COST_METRICS_TAB_DATA,
  setFinTechOpsCostMetricsDashboardData: () => {},
  finTechOpsCostMetricsMonthList: [],
  setFinTechOpsCostMetricsMonthList: () => {}
};

const CostMetricsContext = createContext<CostMetricsContextValue>(defaultValue);

export const useCostMetrics = () => useContext(CostMetricsContext);

interface Props {
  children: React.ReactNode;
}

export const CostMetricsProvider = ({ children }: Props) => {
  const { data, loading, error } = useCostMetricsData();
  const { displayNotification } = useNotification();

  const [parsed, setParsed] = useState<CostMetricsParsedDetails>(EMPTY_PARSED);
  const [monthList, setMonthList] = useState<string[]>([]);
  const [dashboardData, setDashboardData] = useState<CostMetricsDashboardItems>(INITIAL_COST_METRICS_TAB_DATA);
  const [status, setStatus] = useState<FiltersStatus>('loading');

  // Mirror hook state → legacy-shaped context. Preserves order of setters from
  // the original getCostMetricsData: monthList, parsed, then status='finished'.
  useEffect(() => {
    if (loading) {
      setStatus('loading');
      return;
    }
    if (error) {
      logger.error(`Unable to load ${FinTechOpsCategories.costMetrics} data`, error);
      setStatus('error');
      displayNotification('error', FinTechOpsMessages.APIError, TICKETS_FOR_OE_ERROR_URL);
      return;
    }
    if (data) {
      const months = getUniqueFieldValues(data.data, 'metrics_date_MMM_YYYY') as string[];
      setMonthList(months);
      setParsed(data);
      setStatus('finished');
    }
  }, [loading, error, data, displayNotification]);

  const setFinTechOpsCostMetricsDataStatus = useCallback((s: FiltersStatus) => setStatus(s), []);
  const setFinTechOpsCostMetricsParsedData = useCallback((d: CostMetricsParsedDetails) => setParsed(d), []);
  const setFinTechOpsCostMetricsDashboardData = useCallback((d: CostMetricsDashboardItems) => setDashboardData(d), []);
  const setFinTechOpsCostMetricsMonthList = useCallback((m: string[]) => setMonthList(m), []);

  const value = useMemo<CostMetricsContextValue>(
    () => ({
      finTechOpsCostMetricsDataStatus: status,
      setFinTechOpsCostMetricsDataStatus,
      finTechOpsCostMetricsParsedData: parsed,
      setFinTechOpsCostMetricsParsedData,
      finTechOpsCostMetricsDashboardData: dashboardData,
      setFinTechOpsCostMetricsDashboardData,
      finTechOpsCostMetricsMonthList: monthList,
      setFinTechOpsCostMetricsMonthList
    }),
    [
      status,
      parsed,
      dashboardData,
      monthList,
      setFinTechOpsCostMetricsDataStatus,
      setFinTechOpsCostMetricsParsedData,
      setFinTechOpsCostMetricsDashboardData,
      setFinTechOpsCostMetricsMonthList
    ]
  );

  return <CostMetricsContext.Provider value={value}>{children}</CostMetricsContext.Provider>;
};
